import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Coffee as CoffeeIcon, Wifi, Users, ArrowRight, Clock } from "lucide-react";
import Reveal from "../components/site/Reveal";
import Seo, { SITE_URL } from "../components/site/Seo";
import { IMAGES, LOGOS, COFFEE_MENU } from "../lib/site-data";

const COFFEE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "CafeOrCoffeeShop",
  name: "Ocean Oasis Coffee",
  url: `${SITE_URL}/coffee`,
  description:
    "Specialty coffee house at Sundus Plaza, Mogadishu — espresso, pour over, refreshments, pastries, a meeting space and a relaxation lounge.",
  telephone: "+252610424241",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Sundus Plaza, Bondhere District",
    addressLocality: "Mogadishu",
    addressCountry: "SO",
  },
  openingHours: "Mo-Su 07:00-22:00",
  servesCuisine: "Coffee, refreshments, pastries",
  parentOrganization: { "@type": "Organization", name: "Ocean Oasis", url: SITE_URL },
};

export default function Coffee() {
  return (
    <main data-testid="page-coffee" className="overflow-hidden">
      <Seo
        title="Ocean Oasis Coffee | Specialty Coffee House & Meeting Space in Mogadishu"
        description="Your oasis of relaxation at Sundus Plaza, Mogadishu — specialty East African coffee, refreshments, pastries, fast Wi-Fi and a reserved meeting space. Open every day 7 AM – 10 PM."
        path="/coffee"
        jsonLd={COFFEE_JSONLD}
      />
      {/* HERO — editorial split */}
      <section className="relative bg-[#fdf9f2]">
        <div className="grid lg:grid-cols-12 min-h-[100vh]">
          <div className="lg:col-span-6 flex items-center pt-32 pb-16 lg:pb-24 px-6 md:px-12 lg:px-16 xl:px-24">
            <div className="max-w-xl w-full">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="font-label text-[#f59e0b] mb-8 flex items-center gap-3"
              >
                <span className="w-10 h-px bg-[#f59e0b]" />
                Ocean Oasis · Division 03
              </motion.p>
              <motion.img
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                src={LOGOS.coffee}
                alt="Ocean Oasis Coffee logo"
                className="w-full max-w-md h-auto object-contain mix-blend-multiply"
              />
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-[#0a3d7a] leading-[0.95] mt-8 italic"
                style={{ fontSize: "clamp(2.2rem, 4.5vw, 4rem)" }}
              >
                Your <span className="not-italic text-[#f59e0b]">oasis</span> of relaxation.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="font-body text-[#0a3d7a]/80 text-base md:text-lg leading-relaxed mt-6"
              >
                Specialty coffee, fresh refreshments, refined snacks — served
                in a calm, well-designed room built for conversation, focus
                and the small joy of a perfect cup.
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.55 }}
                className="font-label text-[#f59e0b] mt-8"
              >
                Open every day · 7:00 AM – 10:00 PM
              </motion.p>
            </div>
          </div>

          <div className="lg:col-span-6 relative min-h-[55vh] lg:min-h-full overflow-hidden bg-[#0a3d7a]">
            <img
              src={IMAGES.coffeeInterior}
              alt="Warm coffee shop scene"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#0a3d7a]/5 to-[#0a3d7a]/25" />
          </div>
        </div>
      </section>

      {/* QUICK STATS */}
      <section className="py-16 bg-[#fdf9f2] border-y border-[rgba(10,61,122,0.12)]">
        <div className="container-x">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { Icon: CoffeeIcon, t: "Specialty Beans", d: "East African origins" },
              { Icon: Wifi, t: "Fast Wi-Fi", d: "Work without friction" },
              { Icon: Users, t: "Meeting Space", d: "Reserved on request" },
              { Icon: Clock, t: "Every Day", d: "7:00 AM – 10:00 PM" },
            ].map((s, i) => (
              <Reveal key={s.t} delay={i * 0.06}>
                <s.Icon size={22} className="text-[#f59e0b] mb-3" strokeWidth={1.5} />
                <p className="font-display text-xl md:text-2xl text-[#0a3d7a] leading-tight">
                  {s.t}
                </p>
                <p className="font-body text-xs text-[#0a3d7a]/65 mt-1">
                  {s.d}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MENU EDITORIAL */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-10 mb-16">
              <div className="lg:col-span-7">
                <p className="font-label text-[#f59e0b]">The Menu</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a3d7a] leading-[1.02]">
                  Slow craft. <em className="italic">Sincere service.</em>
                </h2>
              </div>
              <div className="lg:col-span-4 lg:col-start-9 lg:pt-3">
                <p className="font-body text-base text-[#0a3d7a]/70 leading-relaxed">
                  A short, well-considered list — refreshed seasonally and made
                  by people who genuinely care about what they serve.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Editorial overlapping grid */}
          <div className="space-y-10">
            {COFFEE_MENU.map((item, i) => {
              const reverse = i % 2 === 1;
              return (
                <Reveal key={item.name}>
                  <div
                    className={`grid lg:grid-cols-12 gap-6 lg:gap-12 items-center ${
                      reverse ? "lg:[direction:rtl]" : ""
                    }`}
                  >
                    <div className="lg:col-span-6 [direction:ltr]">
                      <div className="img-zoom aspect-[4/3] overflow-hidden bg-[#0a3d7a]">
                        <img
                          src={item.image}
                          alt={`${item.name} — Ocean Oasis Coffee, Mogadishu`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="lg:col-span-5 [direction:ltr]">
                      <p className="font-label text-[#f59e0b] mb-3">
                        Menu · {String(i + 1).padStart(2, "0")}
                      </p>
                      <h3 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0a3d7a] leading-[1.04] mb-4">
                        {item.name}
                      </h3>
                      <p className="font-body text-base text-[#0a3d7a]/75 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* AMBIENT QUOTE */}
      <section className="py-24 md:py-32 bg-[#0a3d7a] text-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <div className="max-w-4xl">
              <blockquote className="font-display italic text-3xl md:text-5xl lg:text-6xl leading-[1.08]">
                "A great café isn't a menu. It's a feeling — and you'll
                recognise it the moment the door closes behind you."
              </blockquote>
              <p className="font-label text-[#f59e0b] mt-10">
                — The Coffee Team
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#fdf9f2]">
        <div className="container-x">
          <Reveal>
            <div className="max-w-3xl">
              <p className="font-label text-[#f59e0b] mb-5">Come Sit With Us</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0a3d7a] leading-[1.02] mb-8">
                Find the oasis at <em className="italic">Sundus Plaza.</em>
              </h2>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="coffee-cta-visit" className="btn-primary">
                  Plan a Visit <ArrowRight size={16} />
                </Link>
                <Link to="/" data-testid="coffee-cta-home" className="btn-outline">
                  Back to Group
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
